'use client'
import { useEffect, useRef, useState } from 'react'
import { useLanguage } from '@/lib/context/LanguageContext'
import { toPersianNums, toPersianSuffix } from '@/lib/persian'

function format(n: number, target: number, suffix: string, fa: boolean): string {
  let display: string
  if (target >= 10000) {
    display = Math.round(n / 1000) + (fa ? ' هزار' : 'K')
  } else if (target >= 1000) {
    display = (Math.round(n / 100) / 10).toFixed(1) + (fa ? ' هزار' : 'K')
  } else {
    display = n.toString()
  }
  const out = display + suffix
  if (!fa) return out
  return toPersianNums(display) + toPersianSuffix(suffix)
}

export default function CountUp({
  target,
  suffix = '',
  duration = 2000,
}: {
  target: number
  suffix?: string
  duration?: number
}) {
  const [count, setCount] = useState(0)
  const ref = useRef<HTMLSpanElement>(null)
  const started = useRef(false)
  const { lang } = useLanguage()
  const fa = lang === 'fa'

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true
          const startTime = Date.now()
          const tick = () => {
            const elapsed = Date.now() - startTime
            const progress = Math.min(elapsed / duration, 1)
            const eased = 1 - Math.pow(1 - progress, 3)
            setCount(Math.round(eased * target))
            if (progress < 1) requestAnimationFrame(tick)
          }
          requestAnimationFrame(tick)
        }
      },
      { threshold: 0.3 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [target, duration])

  return <span ref={ref}>{format(count, target, suffix, fa)}</span>
}
